// Pure, testable serving-initialisation logic for FoodConfirm.
//
// Fixes the BARCODE/BRANDED bug where a labeled multi-item serving (e.g. "8 fl oz" or "3 meatballs")
// arrives with a serving OPTION of size:1 that carries the nutrition for the WHOLE labeled serving.
// The screen then shows "1 fl oz" / "1 meatball" with the full-serving calories. The authoritative
// serving_size must win for the quantity, and the per-unit nutrition must be rescaled so the initial
// total still equals ONE labeled serving.
//
// SCOPE: intentionally narrow. It fires ONLY for a barcode/branded item whose matching option (a
// safe continuous OR count unit) says size:1 while the top-level serving_size holds the real labeled
// amount (> 1) AND the option's nutrition corresponds to the COMPLETE labeled serving (not a single
// unit). A real one-item serving (1 bar / 1 patty, serving_size == 1) and a genuine per-single-unit
// option are left EXACTLY as before.

var _eq = function (a, b) {
  return String(a == null ? '' : a).trim().toLowerCase() === String(b == null ? '' : b).trim().toLowerCase();
};
var _num = function (v) { return (typeof v === 'number' && isFinite(v) && v > 0) ? v : null; };

// Safe count units the normalization is generalized to (singular canonical form).
var _COUNT_UNITS = ['meatball', 'piece', 'nugget', 'link', 'cookie', 'cracker', 'bar', 'slice'];

// Normalize equivalent unit spellings AND singular/plural so "fl oz"=="floz", "meatball"=="meatballs".
export function normalizeUnit(u) {
  var s = String(u == null ? '' : u).trim().toLowerCase();
  if (s === 'floz' || s === 'fl' || s === 'fl.' || s === 'fl oz' || s === 'fl. oz' || s === 'fl-oz'
      || s === 'fluid ounce' || s === 'fluid ounces') return 'fl oz';
  if (s === 'gram' || s === 'grams' || s === 'g') return 'g';
  if (s === 'ounce' || s === 'ounces' || s === 'oz') return 'oz';
  if (s === 'milliliter' || s === 'milliliters' || s === 'millilitre' || s === 'millilitres' || s === 'ml') return 'ml';
  if (s === 'liter' || s === 'liters' || s === 'litre' || s === 'litres' || s === 'l') return 'l';
  if (s === 'kilogram' || s === 'kilograms' || s === 'kg') return 'kg';
  if (s === 'patty' || s === 'patties') return 'patty';           // irregular plural
  for (var i = 0; i < _COUNT_UNITS.length; i++) {
    if (s === _COUNT_UNITS[i] || s === _COUNT_UNITS[i] + 's') return _COUNT_UNITS[i];
  }
  return s;
}

// Units the reconciliation is allowed to normalize: continuous measurements + safe count units.
var _NORMALIZABLE = { 'fl oz': 1, ml: 1, g: 1, oz: 1, l: 1, kg: 1,
  meatball: 1, piece: 1, nugget: 1, patty: 1, link: 1, cookie: 1, cracker: 1, bar: 1, slice: 1 };
function _isNormalizableUnit(u) { return !!_NORMALIZABLE[normalizeUnit(u)]; }

// A barcode/branded product carries a brand and/or a FatSecret branded external id (or an explicit
// barcode/branded source). `isPackagedItem` in FoodConfirm is hardcoded false today, so this is the
// reliable way to identify the response shape the barcode bug comes from.
export function isBrandedOrBarcode(food) {
  if (!food) return false;
  if (food.brand) return true;
  var ext = String(food.external_id || '');
  if (/^fs_branded_/.test(ext) || /^barcode_/i.test(ext) || /^upc_/i.test(ext) || /^gtin_/i.test(ext)) return true;
  var src = String(food.source || '').toUpperCase();
  var cat = String(food.category || '').toUpperCase();
  return src === 'BARCODE' || src === 'BRANDED' || cat === 'BRANDED';
}

// Distinguish a mislabeled size-1 option that carries the COMPLETE labeled serving (bug) from a
// genuine per-single-unit option (legit). The top-level `food.nutrition` is per-1-unit (searchItem
// mapper divides by serving_size when > 1), so the malformed option reads ~serving_size times bigger.
function _optionCarriesFullServing(optPerUnit, foodPerUnit, servingSize) {
  var optCal = _num(optPerUnit && optPerUnit.calories);
  var foodCal = _num(foodPerUnit && foodPerUnit.calories);
  if (!optCal || !foodCal || !(servingSize > 1)) return false;
  var full = foodCal * servingSize;
  var nearFull = Math.abs(optCal - full) <= Math.max(1, 0.12 * full);   // ~= complete labeled serving
  var nearUnit = Math.abs(optCal - foodCal) <= Math.max(1, 0.12 * foodCal);  // ~= a single unit
  return nearFull && !nearUnit;
}

// Returns { quantity, perUnit } — the initial amount to display and the per-1-unit nutrition to use,
// guaranteed consistent so `quantity * perUnit` equals ONE labeled serving's nutrition and the
// displayed amount matches the true serving size. Every nutrient key on the source payload is
// preserved and proportionally rescaled (not just the four core macros).
export function resolveInitialServing(food, defaultOption, isPackaged) {
  food = food || {};
  var servingSize = _num(food.serving_size) || 1;
  var servingUnit = food.serving_unit;
  var optSize = defaultOption ? _num(defaultOption.size) : null;
  var optUnit = defaultOption ? defaultOption.unit : null;
  var rawPerUnit = (defaultOption && defaultOption.nutrition) || food.nutrition || {};

  var unitMatchesServing = !!(defaultOption && servingUnit && optUnit
    && normalizeUnit(optUnit) === normalizeUnit(servingUnit));
  var applyBarcodeFix = isBrandedOrBarcode(food)
    && unitMatchesServing
    && _isNormalizableUnit(optUnit)
    && optSize === 1
    && servingSize > 1
    && _optionCarriesFullServing(rawPerUnit, food.nutrition, servingSize);

  var rescale = applyBarcodeFix ? (optSize / servingSize) : 1;   // e.g. 1/3 -> per-meatball for a 3-meatball serving

  var quantity;
  if (isPackaged) {
    quantity = 1;
  } else if (applyBarcodeFix) {
    quantity = servingSize;            // authoritative labeled serving beats the bad size:1 option
  } else if (optSize) {
    quantity = optSize;                // ordinary food / genuine per-unit option: keep option size
  } else if (servingSize > 1) {
    quantity = servingSize;
  } else {
    quantity = 1;
  }

  var perUnit = rawPerUnit;
  if (rescale !== 1) {
    perUnit = {};
    for (var k in rawPerUnit) {
      if (Object.prototype.hasOwnProperty.call(rawPerUnit, k)) {
        var v = rawPerUnit[k];
        perUnit[k] = (typeof v === 'number' && isFinite(v)) ? v * rescale : v;
      }
    }
  }

  return { quantity: quantity, perUnit: perUnit };
}
